import React, { useCallback, useMemo, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEllipsis,
  faFileInvoiceDollar,
} from "@fortawesome/pro-regular-svg-icons";
import { faTimes } from "@fortawesome/pro-solid-svg-icons";
import { StoreSwitch } from "@/components/Feature/StoreSwitch";
import {
  PeriodDataPicker,
  PeriodSearchHandler,
} from "@/components/Feature/DatePicker";
import { useAsync, useUpdateEffect } from "react-use";
import { SwaggerAPI } from "@/api/hunger.api";
import {
  MgtResourceDto,
  MgtStoreResourceCommissionRateDto,
  MgtStoreResourcePerformanceTargetAndBonusesDto,
  MgtStoreResourceSalaryAnalyzeInCommissionByBillTypeDto,
  MgtUpdateStoreResourceCommissionRateReq,
  MgtUpdateStoreResourceTargetAndBonusesReq,
  SearchDateTimeRange,
} from "@/api/swagger";
import { getDateTimeRangePayload } from "@/helper/dateHelper";
import Table, { Column } from "@/components/Feature/Table";
import { truncateNumber } from "@/helper/formatter";
import { hungerMoment } from "@/moment";
import Flexbox from "@/components/General/Flexbox";
import useMediaQuery from "@/hooks/useMediaQuery";
import classNames from "classnames";
import useClickOutside from "@/hooks/useClickOutside/useClickOutside";
import Modal from "@/components/General/Modal/Modal";
import { isNotSet, isSet } from "@/tools/format.checker";
import { SubmitHandler, useForm } from "react-hook-form";
import { cloneDeep } from "lodash";
import { Input } from "@/components/General/InputFields/TextFields";
import { digitValidator } from "@/helper/validator";
import { Decimal } from "decimal.js";

const SalaryManagementApp = () => {
  const [storeId, setStoreId] = useState<number | null>(null);
  const [period, setPeriod] = useState<SearchDateTimeRange>(
    getDateTimeRangePayload(hungerMoment().startOf("month"), hungerMoment())
  );

  const onSearch = useCallback<PeriodSearchHandler>(({ startAt, endAt }) => {
    setPeriod(getDateTimeRangePayload(startAt, endAt));
  }, []);

  return (
    <section className="dashboard">
      <h2 className="heading__2 heading__2--title dashboard__heading">
        <span className="heading__2--icon">
          <FontAwesomeIcon icon={faFileInvoiceDollar} />
        </span>
        業績分潤計算
      </h2>
      <StoreSwitch
        className={"mb-6"}
        storeId={storeId}
        onStoreChange={setStoreId}
      />
      <PeriodDataPicker
        onSearch={onSearch}
        className={"mb-5 md:mb-6 p-5 bg-white rounded-xl"}
      />
      <SalaryCalculation storeId={storeId} period={period} />
    </section>
  );
};

export default SalaryManagementApp;

const SalaryCalculation: React.FC<{
  storeId: number | null;
  period: SearchDateTimeRange | null;
}> = ({ storeId, period }) => {
  const [reloadCount, setReloadCount] = useState<number>(0);

  const { value } = useAsync(async () => {
    if (!storeId || !period) return;
    const { data } =
      await SwaggerAPI.searchStoreResourceSalaryCommissionRateAnalyze(storeId, {
        time: period,
      });
    return data.map((d) => ({
      resource: d.resource,
      table: [d.salaryOrder, d.salaryProduct, d.salaryVoucher, d.salaryDeposit],
    }));
  }, [storeId, period, reloadCount]);

  const reload = useCallback(() => {
    setReloadCount((v) => v + 1);
  }, []);

  if (!storeId) return null;

  return (
    <div className={"bg-white rounded-xl"}>
      {value?.map((v) => (
        <ResourceBlockForSalaryCalculation
          storeId={storeId}
          key={v.resource.id}
          resource={v.resource}
          table={v.table}
          reload={reload}
        />
      ))}
    </div>
  );
};

type PopupConfigType =
  // "surcharge"
  "bonus" | "commission";
const billTypeLabel: Record<
  MgtStoreResourceSalaryAnalyzeInCommissionByBillTypeDto["billType"],
  string
> = {
  ORDER: "服務",
  PRODUCT: "商品",
  VOUCHER: "票券",
  DEPOSIT_POINT: "儲值金",
};

const ResourceBlockForSalaryCalculation: React.FC<{
  storeId: number;
  resource: MgtResourceDto;
  table: Array<MgtStoreResourceSalaryAnalyzeInCommissionByBillTypeDto>;
  reload: () => void;
}> = ({ storeId, resource, table, reload }) => {
  const [currentPopupConfig, setCurrentPopupConfig] =
    useState<PopupConfigType | null>(null);

  const columns = useMemo<
    Array<Column<MgtStoreResourceSalaryAnalyzeInCommissionByBillTypeDto>>
  >(
    () => [
      {
        header: "項目",
        accessor: "billType",
        formatter: ({ billType }) => billTypeLabel[billType],
      },
      {
        header: "達標獎金",
        accessor: "bonus",
        formatter: ({ bonus }) => truncateNumber(bonus),
      },
      {
        header: "指定抽成",
        accessor: "salarySpecifyRate",
        formatter: ({ salarySpecifyAmountWithRate, salarySpecifyRate }) =>
          `${truncateNumber(salarySpecifyAmountWithRate)}(${Decimal.mul(
            salarySpecifyRate,
            100
          ).toString()}%)`,
      },
      {
        header: "不指定抽成",
        accessor: "salaryUnSpecifyRate",
        formatter: ({ salaryUnSpecifyAmountWithRate, salaryUnSpecifyRate }) =>
          `${truncateNumber(salaryUnSpecifyAmountWithRate)}(${Decimal.mul(
            salaryUnSpecifyRate,
            100
          ).toString()}%)`,
      },
      {
        header: "總計",
        accessor: "sum",
        formatter: ({ sum }) => sum,
      },
    ],
    []
  );

  const summary = useMemo(
    () =>
      table.reduce((acc, cur) => {
        acc += cur.sum;
        return acc;
      }, 0),
    [table]
  );

  const closePopup = useCallback(() => {
    setCurrentPopupConfig(null);
    reload();
  }, []);

  return (
    <React.Fragment>
      {currentPopupConfig && (
        <ConfigFormDispatcher
          currentPopupConfig={currentPopupConfig}
          storeId={storeId}
          resource={resource}
          close={closePopup}
        />
      )}
      <div className={"relative md:mb-6"}>
        <Flexbox
          className={
            "py-[1.125rem] px-5 md:items-center border-b border-grey-4"
          }
        >
          <Flexbox
            direction={"column"}
            className={"md:flex-row md:items-center"}
          >
            <p
              className={
                "mb-0.5 md:mb-0 md:mr-4 text-2xl font-semibold text-primary-pink"
              }
            >
              {resource.name}
            </p>
            <p className={"font-medium text-grey-3"}>
              NT${truncateNumber(summary)}
            </p>
          </Flexbox>
          <ConfigPopupOption
            currentPopupConfig={currentPopupConfig}
            setCurrentPopupConfig={setCurrentPopupConfig}
          />
        </Flexbox>
        <Table columns={columns} data={table} />
      </div>
    </React.Fragment>
  );
};

const popupConfigLabel: Record<PopupConfigType, string> = {
  // surcharge: "手續費扣除設定",
  bonus: "業績目標與獎金",
  commission: "抽成方式",
};
const popupConfigLabelOptions = Object.keys(
  popupConfigLabel
) as Array<PopupConfigType>;
const ConfigPopupOption: React.FC<{
  currentPopupConfig: PopupConfigType | null;
  setCurrentPopupConfig: React.Dispatch<
    React.SetStateAction<PopupConfigType | null>
  >;
}> = ({ currentPopupConfig, setCurrentPopupConfig }) => {
  const [showDropdown, setShowDropdown] = useState(false);

  const desktop = useMediaQuery("(min-width: 768px)");
  const dropdownRef = useRef<HTMLDivElement>(null);
  useClickOutside(dropdownRef.current, () => {
    setShowDropdown(false);
  });

  const toggleTrigger = useMemo(() => {
    const trigger = (key: PopupConfigType) => {
      setCurrentPopupConfig(key);
      setShowDropdown(false);
    };
    return {
      // surcharge: () => trigger("surcharge"),
      bonus: () => trigger("bonus"),
      commission: () => trigger("commission"),
      triggerDropdown: () => setShowDropdown((v) => !v),
    };
  }, []);

  const onDropdownClick = useCallback<
    React.MouseEventHandler<HTMLButtonElement>
  >(
    (event) => {
      const id = event.currentTarget.id as PopupConfigType;
      if (toggleTrigger[id]) toggleTrigger[id]();
      else throw new Error(`Unknown id: ${id}`);
    },
    [toggleTrigger]
  );

  if (desktop)
    return (
      <Flexbox className={"ml-auto"}>
        {popupConfigLabelOptions.map((key) => (
          <button
            type={"button"}
            id={key}
            key={key}
            onClick={onDropdownClick}
            className={classNames(
              "mr-6 last:mr-0",
              "px-4",
              "h-[1.875rem]",
              "text-green",
              "border border-green rounded-full"
            )}
          >
            {popupConfigLabel[key as PopupConfigType]}
          </button>
        ))}
      </Flexbox>
    );

  return (
    <Flexbox
      direction={"column"}
      align={"end"}
      className={"absolute top-4 right-1"}
    >
      <button
        type={"button"}
        className={"w-10 h-10 text-grey-2"}
        onClick={toggleTrigger.triggerDropdown}
      >
        <FontAwesomeIcon className={"text-lg"} icon={faEllipsis} />
      </button>
      <div
        ref={dropdownRef}
        style={{
          height: showDropdown ? dropdownRef.current?.scrollHeight : 0,
        }}
        className={classNames(
          "z-[999]",
          "w-[17rem]",
          "rounded-lg shadow-3xl bg-white",
          "overflow-hidden transition-[height] duration-300"
        )}
      >
        <Flexbox direction={"column"} className={"py-2"}>
          {popupConfigLabelOptions.map((key) => {
            const isActive = currentPopupConfig === key;

            return (
              <button
                type={"button"}
                id={key}
                key={key}
                onClick={onDropdownClick}
                className={classNames(
                  "py-2 px-4",
                  "w-full",
                  "text-left",
                  "hoverable:hover:bg-primary-pink hoverable:hover:bg-opacity-5",
                  isActive
                    ? [
                        "bg-primary-pink bg-opacity-5",
                        "text-primary-pink font-medium",
                      ]
                    : ["text-grey-2"]
                )}
              >
                {popupConfigLabel[key as PopupConfigType]}
              </button>
            );
          })}
        </Flexbox>
      </div>
    </Flexbox>
  );
};

const PopupConfigContainer: React.FC<
  {
    currentPopupConfig: PopupConfigType;
    resource: MgtResourceDto;
    close: () => void;
  } & {
    onSubmit?: SubmitHandler<any>;
  }
> = ({ currentPopupConfig, resource, close, children, onSubmit }) => {
  return (
    <Modal className={"md:py-12"}>
      <Flexbox
        as={"form"}
        direction={"column"}
        className={
          "w-full max-w-[40rem] h-stretch md:h-auto md:max-h-full bg-white overflow-hidden md:rounded-xl"
        }
        onSubmit={onSubmit}
      >
        <Flexbox
          direction={"column"}
          align={"start"}
          className={"py-4 px-6 md:pl-12 bg-grey-5"}
        >
          <Flexbox
            as={"button"}
            type={"button"}
            onClick={close}
            className={"self-end w-4 text-grey-3"}
          >
            <FontAwesomeIcon icon={faTimes} className={"w-full text-2xl"} />
          </Flexbox>
          <p className={"mb-1 text-2xl font-semibold"}>
            {popupConfigLabel[currentPopupConfig]}
          </p>
          <p className={"text-sm font-medium text-grey-2"}>{resource.name}</p>
        </Flexbox>
        <div className={"flex-1 py-8 px-6 md:px-12 overflow-scroll"}>
          {children}
        </div>
        <Flexbox
          align={"center"}
          justify={"center"}
          className={
            "flex-shrink-0 px-6 pt-6 pb-12 md:pb-6 border-t border-grey-4"
          }
        >
          <button
            type={"submit"}
            className={"w-full max-w-[21.375rem] btn btn__primary rounded-full"}
          >
            儲存
          </button>
        </Flexbox>
      </Flexbox>
    </Modal>
  );
};

interface FormProps {
  storeId: number;
  resource: MgtResourceDto;
  close: () => void;
  currentPopupConfig: PopupConfigType;
}

const ConfigFormDispatcher: React.FC<FormProps> = ({
  storeId,
  resource,
  currentPopupConfig,
  close,
}) => {
  switch (currentPopupConfig) {
    // case "surcharge":
    case "bonus":
      return (
        <BonusConfigForm
          currentPopupConfig={currentPopupConfig}
          storeId={storeId}
          resource={resource}
          close={close}
        />
      );
    case "commission":
      return (
        <CommissionConfigForm
          currentPopupConfig={currentPopupConfig}
          storeId={storeId}
          resource={resource}
          close={close}
        />
      );
    default:
      return null;
  }
};

const BonusConfigForm: React.FC<FormProps> = ({
  currentPopupConfig,
  storeId,
  resource,
  close,
}) => {
  interface FormValues
    extends Record<
      MgtStoreResourcePerformanceTargetAndBonusesDto["type"],
      Omit<MgtUpdateStoreResourceTargetAndBonusesReq, "type">
    > {}

  const order = useRef<
    Array<MgtStoreResourcePerformanceTargetAndBonusesDto["type"]>
  >(["ORDER", "PRODUCT", "VOUCHER", "DEPOSIT_POINT"]);
  const { value } = useAsync(async () => {
    if (isNotSet(storeId)) return;
    const { data } = await SwaggerAPI.getTargetAndBonusesConfig(
      storeId,
      resource.id
    );
    return order.current
      .map((type) => data.find((d) => d.type === type) ?? null)
      .filter(isSet);
  }, [storeId, resource.id]);
  const {
    reset,
    register,
    handleSubmit,
    formState: { errors, touched },
  } = useForm<FormValues>({ mode: "all" });

  useUpdateEffect(() => {
    if (!value) return;
    reset(
      value.reduce((acc, { type, targetPerformance, targetBonus }) => {
        const next = cloneDeep(acc);
        next[type] = {
          targetPerformance,
          targetBonus,
        };
        return next;
      }, {} as Partial<FormValues>)
    );
  }, [value]);

  const onSubmit = useCallback<SubmitHandler<FormValues>>(async (data) => {
    await SwaggerAPI.updateTargetAndBonusesConfig(
      storeId,
      resource.id,
      Object.keys(data).map((key) => ({
        type: key as MgtStoreResourcePerformanceTargetAndBonusesDto["type"],
        ...data[key as MgtStoreResourcePerformanceTargetAndBonusesDto["type"]]!,
      }))
    );
    close();
  }, []);

  return (
    <PopupConfigContainer
      onSubmit={handleSubmit(onSubmit)}
      currentPopupConfig={currentPopupConfig}
      close={close}
      resource={resource}
    >
      <div>
        <div className={"space-y-6 md:space-y-4 divide-y divide-grey-4"}>
          {value?.map(({ type }) => (
            <div
              key={type}
              className={
                "md:flex md:items-end pt-6 md:pt-4 first:pt-0 space-y-6 md:space-y-0"
              }
            >
              <p
                className={"md:leading-[3.125rem] md:w-[4.5rem] font-semibold"}
              >
                {billTypeLabel[type]}
              </p>
              <div className={"md:mr-6 md:flex-1"}>
                <p className={"mb-2 text-sm text-grey-2 font-medium"}>
                  目標金額
                </p>
                <Input
                  inputMode={"numeric"}
                  ref={register({
                    required: "此欄位為必填。",
                    validate: digitValidator,
                    min: {
                      value: 0,
                      message: "請輸入大於0的數字。",
                    },
                    max: {
                      value: 100,
                      message: "請輸入小於100的數字。",
                    },
                  })}
                  name={`${type}.targetPerformance`}
                  disabledBorder
                  className={"border-green border bg-green bg-opacity-5"}
                  error={errors[type]?.targetPerformance?.message}
                  touched={touched[type]?.targetPerformance}
                />
              </div>
              <div className={"md:flex-1"}>
                <p className={"mb-2 text-sm text-grey-2 font-medium"}>
                  達標獎金
                </p>
                <Input
                  inputMode={"numeric"}
                  ref={register({
                    required: "此欄位為必填。",
                    validate: digitValidator,
                    min: {
                      value: 0,
                      message: "請輸入大於0的數字。",
                    },
                    max: {
                      value: 100,
                      message: "請輸入小於100的數字。",
                    },
                  })}
                  name={`${type}.targetBonus`}
                  error={errors[type]?.targetBonus?.message}
                  touched={touched[type]?.targetBonus}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </PopupConfigContainer>
  );
};

const CommissionConfigForm: React.FC<FormProps> = ({
  currentPopupConfig,
  storeId,
  resource,
  close,
}) => {
  interface FormValues
    extends Record<
      MgtStoreResourceCommissionRateDto["type"],
      Omit<MgtUpdateStoreResourceCommissionRateReq, "type">
    > {}

  const order = useRef<
    Array<MgtStoreResourcePerformanceTargetAndBonusesDto["type"]>
  >(["ORDER", "PRODUCT", "VOUCHER", "DEPOSIT_POINT"]);
  const { value } = useAsync(async () => {
    if (isNotSet(storeId)) return;
    const { data } = await SwaggerAPI.getCommissionRateConfig(
      storeId,
      resource.id
    );
    return order.current
      .map((type) => data.find((d) => d.type === type) ?? null)
      .filter(isSet);
  }, [storeId, resource.id]);
  const {
    reset,
    register,
    handleSubmit,
    formState: { errors, touched },
  } = useForm<FormValues>({ mode: "all" });

  useUpdateEffect(() => {
    if (!value) return;
    reset(
      value.reduce(
        (acc, { type, specificCommissionRate, unspecificCommissionRate }) => {
          const next = cloneDeep(acc);
          next[type] = {
            specificCommissionRate: Decimal.mul(
              specificCommissionRate,
              100
            ).toNumber(),
            unspecificCommissionRate: Decimal.mul(
              unspecificCommissionRate,
              100
            ).toNumber(),
          };
          return next;
        },
        {} as Partial<FormValues>
      )
    );
  }, [value]);

  const onSubmit = useCallback<SubmitHandler<FormValues>>(async (data) => {
    await SwaggerAPI.updateCommissionRateConfig(
      storeId,
      resource.id,
      Object.keys(data).map((key) => {
        const { specificCommissionRate, unspecificCommissionRate } =
          data[key as MgtStoreResourcePerformanceTargetAndBonusesDto["type"]]!;
        return {
          type: key as MgtStoreResourcePerformanceTargetAndBonusesDto["type"],
          specificCommissionRate: Decimal.div(
            specificCommissionRate,
            100
          ).toNumber(),
          unspecificCommissionRate: Decimal.div(
            unspecificCommissionRate,
            100
          ).toNumber(),
        };
      })
    );
    close();
  }, []);

  return (
    <PopupConfigContainer
      onSubmit={handleSubmit(onSubmit)}
      currentPopupConfig={currentPopupConfig}
      close={close}
      resource={resource}
    >
      <div>
        <div className={"space-y-6 md:space-y-4 divide-y divide-grey-4"}>
          {value?.map(({ type }) => (
            <div
              key={type}
              className={
                "md:flex md:items-end pt-6 md:pt-4 first:pt-0 space-y-6 md:space-y-0"
              }
            >
              <p
                className={"md:leading-[3.125rem] md:w-[4.5rem] font-semibold"}
              >
                {billTypeLabel[type]}
              </p>
              <div className={"md:mr-6 md:flex-1"}>
                <p className={"mb-2 text-sm text-grey-2 font-medium"}>
                  指定抽成
                </p>
                <div className={"relative"}>
                  <Input
                    inputMode={"numeric"}
                    ref={register({
                      required: "此欄位為必填。",
                      validate: digitValidator,
                      min: {
                        value: 0,
                        message: "請輸入大於0的數字。",
                      },
                      max: {
                        value: 100,
                        message: "請輸入小於100的數字。",
                      },
                    })}
                    name={`${type}.specificCommissionRate`}
                    error={errors[type]?.specificCommissionRate?.message}
                    touched={touched[type]?.specificCommissionRate}
                  />
                  <p
                    className={
                      "absolute top-0 right-4 h-[3.125rem] leading-[3.125rem]"
                    }
                  >
                    %
                  </p>
                </div>
              </div>
              <div className={"md:flex-1"}>
                <p className={"mb-2 text-sm text-grey-2 font-medium"}>
                  不指定抽成
                </p>
                <div className={"relative"}>
                  <Input
                    inputMode={"numeric"}
                    ref={register({
                      required: "此欄位為必填。",
                      validate: digitValidator,
                      min: {
                        value: 0,
                        message: "請輸入大於0的數字。",
                      },
                      max: {
                        value: 100,
                        message: "請輸入小於100的數字。",
                      },
                    })}
                    name={`${type}.unspecificCommissionRate`}
                    error={errors[type]?.unspecificCommissionRate?.message}
                    touched={touched[type]?.unspecificCommissionRate}
                  />
                  <p
                    className={
                      "absolute top-0 right-4 h-[3.125rem] leading-[3.125rem]"
                    }
                  >
                    %
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </PopupConfigContainer>
  );
};
